import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  Col,
  Collapse,
  FloatingLabel,
  Form,
  FormLabel,
  InputGroup,
  Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faPen } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import UserIcon from "../../assets/icons/contact_edit.svg2";
import AttachIcon from "../../assets/icons/attach.svg2";

import HelpButton from "../HelpContent/HelpButton";
import Input, { validateEmail } from "../Builder/nodes/Logic/Inputs/Input";
import {
  useGetNewUserDataQuery,
  useGetUserDataQuery,
  useGetUsersSettingsProductsFilterDataQuery,
  useLazyGetNewUserDataQuery,
  useLazyGetUserDataQuery,
  useUpdateUserDataMutation,
} from "../store/slices/settingsSlice";
import { parsePhoneNumber } from "react-phone-number-input";
import { Link } from "react-router-dom";

const { countries, defaultCountry, defaultTZ } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

const newUser = {
  username: "",
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  active: "Y",
  timeout: null,
  locked: "N",
  role_id: null,
  attempts: null,
  temp_pw: "N",
  last_attempt: null,
  last_ip: null,
  last_client_id: 1,
  temp_code: null,
  temp_valid: null,
  updated_by: 0,
  country_id: defaultCountry.id,
  country: "44",
  updated_when: null,
  updated_at: "",
  created_at: "",
  help_tips: 1,
  contact_id: 0,
  notes: null,
  initials: null,
  tour_guide: "N",
  last_action: null,
  last_comm_used: "1",
  superadmin_permissions: null,
  is_welcome_email_sent: 0,
  departments: [],
  products: [],
  user_roles: [],
  send_email_notification: 1,
};

export default function AddUsersModal({ userId }) {
  const [addUser, setAddUser] = useState(newUser);
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    setErrors({});
  };

  const [data, setData] = useState({});
  const [getUserData] = useLazyGetUserDataQuery();
  const [getNewUserData] = useLazyGetNewUserDataQuery();
  const { data: products } = useGetUsersSettingsProductsFilterDataQuery(null, {
    skip: !show,
  });

  const handleShow = async () => {
    if (userId) {
      const { data: userData } = await getUserData(userId);
      if (userData) {
        setAddUser({
          ...userData.User,
          products: userData.User.products_raw?.map((p) => p.id),
          user_roles: userData.User.user_roles?.map((p) => p.id),
          mobile: parsePhoneNumber(
            userData.User.mobile,
            (
              countries.find((c) => c.id === addUser.country_id) ||
              defaultCountry
            ).country_code
          ).nationalNumber,
          send_email_notification: 0,
        });
        setData(userData);
        setShow(true);
      }
    } else {
      const { data: newUserData } = await getNewUserData();
      if (!userId && newUserData) {
        setData(newUserData);
        setShow(true);
      }
      getNewUserData();
    }
  };

  const [saveData] = useUpdateUserDataMutation();

  const handleAddUserChange = (e) =>
    setAddUser((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const frontendErrors = {};
  if (!addUser.products || addUser.products.length === 0) {
    frontendErrors.product_name = "Please select at least one product.";
  }
  if (
    ![1, 2].includes(addUser.role_id) &&
    (!addUser.user_roles || addUser.user_roles.length === 0)
  ) {
    frontendErrors.user_roles = "Please select at least one role.";
  }
  if (!addUser.mobile || addUser.mobile.trim() === "") {
    frontendErrors.mobile = "Mobile number is required.";
  }

  const handleSave = async () => {
    if (Object.keys(frontendErrors).length > 0) {
      setErrors(frontendErrors);
      return;
    }
    setErrors({});
    const body = {
      locked: addUser.locked,
      notes: addUser.notes,
      user_id: addUser.id,
      first_name: addUser.first_name,
      initials: addUser.initials,
      last_name: addUser.last_name,
      country_id: addUser.country_id,
      user_email: addUser.email,
      mobile: addUser.mobile,
      products: addUser.products,
      user_roles: addUser.user_roles,
      send_email_notification: addUser.send_email_notification,
      btn_add_as_contact: "N",
      "contact[id]": 0,
      "contact[products][]": 2,
      "contact[telephone]": undefined,
    };
    const res = await saveData({ url: data.formAction, body });

    if (res.error) {
      toast.error(res.error.data.message);
      setErrors(res.error.data.errors);
      return;
    }
    toast.success("Settings saved successfully");
    handleClose();
    setAddUser(newUser);
  };

  const [showNotes, setShowNotes] = useState(false);

  return (
    <>
      {userId ? (
        <Button
          onClick={handleShow}
          className="btn btn-secondary rounded-pill fw-bold btn-sm text-nowrap border-0"
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      ) : (
        <Button
          onClick={handleShow}
          className="btn btn-colored-reverse rounded-pill fw-bold border-0 "
        >
          <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
          Add Users{" "}
          <HelpButton
            helpKey="settings-user-settings-add-users"
            iconClassName="white"
          />
        </Button>
      )}

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        contentClassName="bg-purple-gradient-darker-reverse"
        style={{ minHeight: 300 }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="flex-grow-1">
            <div className="d-flex justify-content-between">
              <h5 className=" d-flex align-items-center">
                <UserIcon width={20} />{" "}
                <span className="ms-2">
                  <span className="fw-bold me-2">
                    {addUser.id ? "Edit" : "Add"} User
                    <HelpButton helpKey="settings-manage-users-add-edit-user" />
                  </span>
                </span>
              </h5>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col>
                <FormLabel className="fw-bold">Required details </FormLabel>
                <HelpButton helpKey="settings-manage-users-add-user-required-details" />
                <Row>
                  <Col xs={4}>
                    <FloatingLabel label="First Name:">
                      <Form.Control
                        placeholder="First Name:"
                        name="first_name"
                        onChange={handleAddUserChange}
                        value={addUser.first_name}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col xs={2}>
                    <FloatingLabel label="Initial:">
                      <Form.Control
                        placeholder="Initial:"
                        name="initials"
                        onChange={handleAddUserChange}
                        value={addUser.initials}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col xs={6}>
                    <FloatingLabel label="Last Name">
                      <Form.Control
                        placeholder="Last Name:"
                        name="last_name"
                        onChange={handleAddUserChange}
                        value={addUser.last_name}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                {errors.first_name && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors.first_name}
                  </div>
                )}{" "}
                {errors.last_name && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors.last_name}
                  </div>
                )}{" "}
                {errors.initials && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors.initials}
                  </div>
                )}
                <div className="bg-white p-2 rounded-3 my-3">
                  <div>Country:</div>
                  <Input
                    inputType="select"
                    onChange={([v]) =>
                      handleAddUserChange({
                        target: { name: "country_id", value: v },
                      })
                    }
                    value={[addUser.country_id]}
                    options={countries.map((c) => ({
                      label: c.country_name,
                      value: c.id,
                    }))}
                  />
                </div>
                <div className="bg-white p-2 rounded-3 my-3">
                  <div>Products:</div>
                  <Input
                    isMulti
                    inputType="select"
                    onChange={(v) =>
                      handleAddUserChange({
                        target: { name: "products", value: v },
                      })
                    }
                    value={addUser.products}
                    options={products?.map((p) => ({
                      label: p.product_name,
                      value: p.id,
                    }))}
                  />
                </div>
                {errors?.product_name && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors?.product_name}
                  </div>
                )}
                <div className="bg-white p-2 rounded-3 my-3">
                  <div>Predefined Roles:</div>
                  {addUser.role_id === 1 ? (
                    <div className="text-wrap fw-bold small bg-colored rounded-pill px-2 text-white d-inline-block mt-2">
                      Super Admin
                    </div>
                  ) : addUser.role_id === 2 ? (
                    <div className="text-wrap fw-bold small bg-colored rounded-pill px-2 text-white d-inline-block mt-2">
                      Controller
                    </div>
                  ) : (
                    <Input
                      inputType="select"
                      isMulti
                      onChange={(v) =>
                        handleAddUserChange({
                          target: { name: "user_roles", value: v },
                        })
                      }
                      value={addUser.user_roles}
                      options={data?.UserRoles?.map((u) => ({
                        label: `${u.name}`,

                        value: u.id,
                      }))}
                      formatOptionLabel={(o) => {
                        const role = data?.UserRoles?.find(
                          (r) => r.id === o.value
                        );
                        if (role) {
                          return (
                            <div className="text-wrap">
                              <div className="fw-bold">
                                {role.name}{" "}
                                {role.clients?.map((c) => (
                                  <span className="badge bg-light ms-1 text-dark">
                                    {c.client_name} ({c.product_type})
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return o.label;
                      }}
                    />
                  )}
                </div>
                {errors?.user_roles && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors?.user_roles}
                  </div>
                )}
                {!addUser.id && (
                  <div className="d-flex align-items-center ms-4">
                    <Form.Check
                      type="switch"
                      label={
                        <div className="mx-2 mt-1">Send email notification</div>
                      }
                      id={`${addUser.id || "new-user"}-send_email_notification`}
                      className="p-2 px-2 my-2 fs-6"
                      checked={addUser.send_email_notification === 1}
                      onChange={() =>
                        setAddUser((prev) => ({
                          ...prev,
                          send_email_notification:
                            prev.send_email_notification === 0 ? 1 : 0,
                        }))
                      }
                    />{" "}
                    <HelpButton helpKey="settings-add-user-send-email-notification" />
                  </div>
                )}
              </Col>
              <Col>
                <FormLabel className="fw-bold">User contact details </FormLabel>
                <HelpButton helpKey="settings-manage-users-add-user-contact-details" />
                <div className="bg-white p-2 rounded-3">
                  <FormLabel>Email:</FormLabel>
                  <Input
                    isEmail
                    onChange={([v]) =>
                      handleAddUserChange({
                        target: { name: "email", value: v },
                      })
                    }
                    value={[addUser.email]}
                  />
                  {addUser.email && !validateEmail(addUser.email) && (
                    <div className="alert alert-secondary">
                      Please enter a valid email address
                    </div>
                  )}
                </div>
                {errors.user_email && (
                  <div className="alert alert-danger p-1 mt-2">
                    {errors.user_email}
                  </div>
                )}
                <FormLabel className="fw-bold mt-2">
                  Do not type the international prefix
                </FormLabel>
                <div className="bg-white p-2 rounded-3">
                  <FormLabel>Phone number:</FormLabel>

                  <InputGroup className="mb-3">
                    <InputGroup.Text>
                      +
                      {
                        (
                          countries.find((c) => c.id === addUser.country_id) ||
                          defaultCountry
                        ).idd
                      }
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Phone number"
                      aria-label="Phone number"
                      value={addUser.mobile}
                      onChange={(e) =>
                        setAddUser((prev) => ({
                          ...prev,
                          mobile: e.target.value,
                        }))
                      }
                    />
                  </InputGroup>
                  {errors.mobile && (
                    <div className="alert alert-danger p-1 mt-2">
                      {errors.mobile}
                    </div>
                  )}
                </div>
                <div className="my-3">
                  <FormLabel className="fw-bold">Notes</FormLabel>
                  <Form.Control
                    as="textarea"
                    placeholder="You can add your notes here"
                    value={addUser.notes}
                    name="notes"
                    onChange={handleAddUserChange}
                    className="p-3"
                  />
                </div>
              </Col>
            </Row>
          </Form>
          <div className="d-flex justify-content-end gap-2">
            <div className="d-flex align-items-center px-2">
              <Link
                to="/settings/users/role/new"
                className="btn btn-colored-reverse rounded-pill fw-bold border-0  "
              >
                <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
                Add New Role
                <HelpButton
                  helpKey="settings-manage-users-roles-add-new-role"
                  iconClassName="text-white rounded-pill ms-2"
                />
              </Link>
            </div>
            <Button
              type="button"
              className="btn btn-colored-reverse rounded-pill fw-bold border-0"
              onClick={handleSave}
            >
              <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
              {userId ? "Save" : "Add User"}
              <HelpButton
                helpKey={
                  userId
                    ? "settings-manage-users-edit-user-save-button"
                    : "settings-manage-users-add-user-add-button"
                }
                iconClassName="text-white rounded-pill ms-2"
              />
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer />
      </Modal>
    </>
  );
}
